import { Component, OnDestroy, inject } from '@angular/core';
import { AuthService } from '@/shared/services/auth.service';
import { ToastService } from '@/shared/services/toast.service';
import { NotificationsService } from '@/shared/services/notifications.service';
import { User, UserRoleTranslations } from '@/shared/models/user';
import { Subscription } from 'rxjs';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AvatarComponent } from '@/shared/components/avatar/avatar.component';
import { LoadingDirective } from '@/shared/directives/loading.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-user-dropdown',
  templateUrl: './user-dropdown.component.html',
  styleUrls: ['./user-dropdown.component.scss'],
  imports: [
    MatCard,
    MatMenuTrigger,
    MatCardContent,
    AvatarComponent,
    MatMenu,
    MatMenuItem,
    LoadingDirective,
    TranslatePipe,
    RouterLink,
  ],
})
export class UserDropdownComponent implements OnDestroy {
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private notifications = inject(NotificationsService);
  // CRAT-1302 — drives the red dot on the avatar and the count in the menu.
  unreadCount = this.notifications.unreadCount;

  user: User;
  userRole: string;
  isLoggingOut = false;
  isReloading = false;
  isDropdownOpen: boolean = false;
  private userSubscription: Subscription;

  ngOnInit(): void {
    this.userSubscription = this.authService.user$.subscribe((user) => {
      this.user = user;
      this.userRole = UserRoleTranslations[this.user.role];
    });
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  async logout() {
    try {
      this.isLoggingOut = true;
      await this.authService.logout(this.authService.user.email);
    } catch (e) {
      this.toast.showError(e, false, '', true);
    } finally {
      this.isLoggingOut = false;
    }
  }

  /**
   * CRAT-764 — user-triggered "Reload Cratebase". Pulls fresh user/company data, then
   * reboots the app on the latest deployed code. Does NOT log out (the auth token is
   * untouched). Best-effort: even if the data refresh fails we still reload — a dead
   * token is already handled by the JWT interceptor, and a code-reload is still useful.
   */
  async reloadApp() {
    try {
      this.isReloading = true;
      await this.authService.refreshUser();
    } catch (e) {
      console.warn('Reload Cratebase: user refresh failed, reloading anyway', e);
    } finally {
      this.reload();
    }
  }

  // Isolated side effect so it can be stubbed in tests.
  reload() {
    window.location.reload();
  }
}
